// b548 — REAL Google "layerTraffic" congestion draped on the Google Photorealistic
// 3D TILES (red/amber/green roads). The default globe is hidden (scene.globe.show=false),
// so 2D imagery must go on tileset.imageryLayers (Cesium 1.107+), NOT viewer.imageryLayers.
// Mints a session via /traffic-session, streams /v1/2dtiles/{z}/{x}/{y}?session=... .
import { createEffect, onCleanup } from "solid-js";
import { useCesium } from "../../cesium/useCesium.ts";
import { groundState } from "./store.ts";
import { PROXY_BASE_URL } from "../../config.ts";
export function GoogleTrafficLayer() {
  const {
    viewer,
    ready
  } = useCesium();
  let layer = null;
  let refresh = null;
  let tries = 0;
  const getTileset = () => window.__tileset;
  const remove = () => {
    const ts = getTileset();
    if (ts && ts.imageryLayers && layer) {
      try {
        ts.imageryLayers.remove(layer, true);
      } catch (_) {}
    }
    layer = null;
  };
  const add = async () => {
    if (!groundState.trafficEnabled || !viewer()) return;
    const ts = getTileset();
    if (!ts || !ts.imageryLayers) {
      // 3D tileset not ready yet — retry a few times
      if (tries++ < 30) setTimeout(() => {
        if (groundState.trafficEnabled) add();
      }, 700);
      return;
    }
    try {
      const res = await fetch(`${PROXY_BASE_URL}/traffic-session`);
      const s = await res.json();
      if (!s || !s.session || !groundState.trafficEnabled) return;
      remove();
      const provider = new Cesium.UrlTemplateImageryProvider({
        url: `${PROXY_BASE_URL}/v1/2dtiles/{z}/{x}/{y}?session=${s.session}`,
        // 2026-08-31 THE ACTUAL SLAB FIX. alpha 0.55 + NEAREST filtering both
        // shipped and neither helped, because the problem was never opacity — it
        // was RESOLUTION. maximumLevel 18 caps how fine a traffic tile Cesium will
        // fetch; past z18 it stops requesting and STRETCHES the last tile it has.
        // At street level that magnifies a few-pixel road line into a solid band
        // covering the whole carriageway. Google Map Tiles serves roadmap/traffic
        // to z22, so let it fetch matching detail instead of upscaling.
        maximumLevel: 22,
        credit: "Google Traffic"
      });
      const l = new Cesium.ImageryLayer(provider);
      // 2026-08-31 (Brockmann, on device: traffic rendering as huge opaque green
      // slabs that buried the map). Two things were wrong at close zoom:
      //  1. alpha 1.0 — the drape was FULLY opaque, so roads read as solid bands
      //     rather than a congestion tint over the imagery underneath.
      //  2. maximumLevel 18 — past that level Cesium UPSAMPLES the last tile it
      //     has, so a few-pixel road line is magnified into a fat blob. On a DPR-3
      //     iPhone that magnification is far more obvious than on desktop.
      // Translucent is also simply how a traffic overlay is meant to read: you
      // must still see the ground truth beneath the congestion colour.
      l.alpha = 0.55;
      // Do not draw the overlay past its native resolution — beyond this Cesium
      // stretches rather than fetches, which is what produced the slabs.
      l.magnificationFilter = Cesium.TextureMagnificationFilter.NEAREST;
      ts.imageryLayers.add(l);
      layer = l;
      const v = viewer();
      if (v) v.scene.requestRender();
      console.log("[GoogleTrafficLayer] live congestion draped on 3D tiles");
    } catch (e) {
      console.error("[GoogleTrafficLayer] failed:", e);
    }
  };
  createEffect(() => {
    const on = groundState.trafficEnabled;
    if (!ready()) return;
    if (on) {
      tries = 0;
      add();
      if (refresh) clearInterval(refresh);
      refresh = setInterval(add, 60 * 60 * 1000); // sessions expire ~hourly
    } else {
      remove();
      if (refresh) {
        clearInterval(refresh);
        refresh = null;
      }
    }
  });
  onCleanup(() => {
    remove();
    if (refresh) clearInterval(refresh);
  });
  return null;
}
export default GoogleTrafficLayer;